import classNames from "classnames";
import React, { FC, useLayoutEffect, useRef, useState } from "react";

import { countFittingChips } from "./fitChips";

export type FitChipItem = {
  key: string;
  label: string;
};

type Props = {
  items: FitChipItem[];
  /** Shows an ✕ on each chip when given. */
  onRemove?: (key: string) => void;
  /** Width kept free after the chips (e.g. for a search box). */
  reserve?: number;
  /** Smaller chips for short boxes (table-style filters). */
  compact?: boolean;
  className?: string;
};

const GAP = 6;

const CHIP_CLASSES =
  "inline-flex max-w-full shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[7px] bg-[#EDE9FE] pl-2.5 font-semibold text-[#5B43D6]";
const MORE_CLASSES =
  "inline-flex shrink-0 items-center whitespace-nowrap rounded-[7px] bg-[#F0EDFF] px-[9px] font-bold text-[#6B4EFF]";
const SIZE_CLASSES = {
  normal: "py-1 text-[12.5px] leading-[18px]",
  compact: "py-0 text-[12px] leading-[18px]",
};

const Chip: FC<{
  item: FitChipItem;
  onRemove?: (key: string) => void;
  measuring?: boolean;
  sizeClasses: string;
}> = ({ item, onRemove, measuring, sizeClasses }) => (
  <span
    className={classNames(
      CHIP_CLASSES,
      sizeClasses,
      onRemove ? "pr-1.5" : "pr-2.5",
    )}
    title={measuring ? undefined : item.label}>
    <span className={"min-w-0 truncate"}>{item.label}</span>
    {onRemove && (
      <span
        role={measuring ? undefined : "button"}
        tabIndex={measuring ? undefined : -1}
        aria-label={measuring ? undefined : `Remove ${item.label}`}
        className={
          "inline-flex shrink-0 cursor-pointer text-[11px] leading-none text-[#A99DE8] hover:text-[#5B43D6]"
        }
        onMouseDown={(event) => {
          // Keep dropdowns from opening when a chip is removed.
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!measuring) onRemove(item.key);
        }}>
        ✕
      </span>
    )}
  </span>
);

/**
 * Selected values as chips ("bubbles"): as many as fit on one line, then a
 * "+N" chip for the rest — only when something is hidden.
 */
export const FitChips: FC<Props> = ({
  items,
  onRemove,
  reserve = 0,
  compact,
  className,
}) => {
  const sizeClasses = SIZE_CLASSES[compact ? "compact" : "normal"];
  const moreClasses = classNames(MORE_CLASSES, sizeClasses);
  const containerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const itemsKey = items.map((item) => `${item.key}:${item.label}`).join("|");
  const removable = !!onRemove;

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;

    const update = () => {
      const chips = Array.from(
        measure.querySelectorAll<HTMLElement>("[data-chip]"),
      ).map((element) => element.getBoundingClientRect().width);
      const moreChips = Array.from(
        measure.querySelectorAll<HTMLElement>("[data-more]"),
      ).map((element) => element.getBoundingClientRect().width);
      const available = container.getBoundingClientRect().width - reserve;
      setVisibleCount(
        countFittingChips(
          chips,
          available,
          GAP,
          (hidden) => moreChips[String(hidden).length - 1] ?? 32,
        ),
      );
    };

    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, [itemsKey, reserve, removable, compact]);

  const shown = items.slice(0, Math.max(visibleCount, items.length ? 1 : 0));
  const hidden = items.length - shown.length;

  return (
    <span
      ref={containerRef}
      className={classNames(
        "relative flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden",
        className,
      )}>
      {shown.map((item) => (
        <Chip
          key={item.key}
          item={item}
          onRemove={onRemove}
          sizeClasses={sizeClasses}
        />
      ))}
      {hidden > 0 && (
        <span
          className={moreClasses}
          title={items
            .slice(shown.length)
            .map((item) => item.label)
            .join(", ")}>
          +{hidden}
        </span>
      )}
      {/* Invisible copies, used to measure how wide each chip is. */}
      <span
        ref={measureRef}
        aria-hidden
        className={
          "pointer-events-none invisible absolute left-0 top-0 flex w-max gap-1.5"
        }>
        {items.map((item) => (
          <span key={item.key} data-chip className={"inline-flex shrink-0"}>
            <Chip
              item={item}
              onRemove={onRemove}
              measuring
              sizeClasses={sizeClasses}
            />
          </span>
        ))}
        <span data-more className={moreClasses}>
          +9
        </span>
        <span data-more className={moreClasses}>
          +99
        </span>
        <span data-more className={moreClasses}>
          +999
        </span>
      </span>
    </span>
  );
};
